import { useEffect, useRef, useMemo, useCallback } from 'react';
import type { CSSProperties } from 'react';

interface VariableProximityTextProps {
	text: string;
	className?: string;
	style?: CSSProperties;
	maxSpacing?: number;
	minSpacing?: number;
	radius?: number;
}

type WordElement =
	| { type: 'space'; key: string; content: string }
	| { type: 'word'; key: string; wordChars: string[]; wordStartIndex: number };

const prefersReducedMotion =
	typeof window !== 'undefined' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const THROTTLE_MS = 32;

export function VariableProximityText({
	text,
	className = '',
	style,
	maxSpacing = 0.15,
	minSpacing = -0.02,
	radius = 150,
}: VariableProximityTextProps) {
	const containerRef = useRef<HTMLSpanElement>(null);
	const letterRefs = useRef<(HTMLSpanElement | null)[]>([]);
	const rafRef = useRef<number | null>(null);
	const mousePosRef = useRef<{ x: number; y: number } | null>(null);
	const lastMousePosRef = useRef<{ x: number; y: number } | null>(null);
	const letterRectsRef = useRef<DOMRect[]>([]);
	const lastRectUpdateRef = useRef(0);

	const wordElements = useMemo<WordElement[]>(() => {
		const words = text.split(/(\s+)/);
		let letterIndex = 0;

		return words.map((word, wordIndex) => {
			if (word.trim() === '') {
				return {
					type: 'space',
					key: `space-${wordIndex}`,
					content: word,
				};
			}

			const wordStartIndex = letterIndex;
			const wordChars = word.split('');

			letterIndex += wordChars.length;

			return {
				type: 'word',
				key: `word-${wordIndex}`,
				wordChars,
				wordStartIndex,
			};
		});
	}, [text]);

	const updateLetterRects = useCallback(() => {
		const letters = letterRefs.current;
		const rects: DOMRect[] = [];

		for (let i = 0; i < letters.length; i++) {
			const letterRef = letters[i];

			if (letterRef) {
				rects[i] = letterRef.getBoundingClientRect();
			}
		}

		letterRectsRef.current = rects;
		lastRectUpdateRef.current = Date.now();
	}, []);

	const updateLetterSpacing = useCallback(() => {
		const letters = letterRefs.current;
		const mousePos = mousePosRef.current;
		const rects = letterRectsRef.current;

		if (!mousePos) {
			for (let i = 0; i < letters.length; i++) {
				const letterRef = letters[i];

				if (letterRef) {
					letterRef.style.letterSpacing = `${minSpacing}em`;
				}
			}
			return;
		}

		const { x: mouseX, y: mouseY } = mousePos;

		for (let i = 0; i < letters.length; i++) {
			const letterRef = letters[i];
			const rect = rects[i];

			if (!letterRef || !rect) {
				continue;
			}

			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;

			const dx = mouseX - centerX;
			const dy = mouseY - centerY;
			const distance = Math.sqrt(dx * dx + dy * dy);

			const normalizedDistance = Math.min(distance / radius, 1);
			const spacing =
				minSpacing + (maxSpacing - minSpacing) * (1 - normalizedDistance);

			letterRef.style.letterSpacing = `${spacing}em`;
		}
	}, [maxSpacing, minSpacing, radius]);

	useEffect(() => {
		if (prefersReducedMotion) {
			return;
		}

		updateLetterRects();
		updateLetterSpacing();

		let lastFrameTime = 0;

		const scheduleUpdate = () => {
			if (rafRef.current !== null) {
				return;
			}

			rafRef.current = requestAnimationFrame((timestamp) => {
				rafRef.current = null;

				if (timestamp - lastFrameTime < THROTTLE_MS) {
					if (
						mousePosRef.current &&
						lastMousePosRef.current &&
						(mousePosRef.current.x !== lastMousePosRef.current.x ||
							mousePosRef.current.y !== lastMousePosRef.current.y)
					) {
						scheduleUpdate();
					}

					return;
				}

				lastFrameTime = timestamp;
				lastMousePosRef.current = mousePosRef.current
					? { ...mousePosRef.current }
					: null;

				updateLetterSpacing();
			});
		};

		const handleMouseMove = (e: MouseEvent) => {
			mousePosRef.current = { x: e.clientX, y: e.clientY };
			scheduleUpdate();
		};

		const handleTouchMove = (e: TouchEvent) => {
			const touch = e.touches[0];
			if (touch) {
				mousePosRef.current = { x: touch.clientX, y: touch.clientY };
				scheduleUpdate();
			}
		};

		const handleResize = () => {
			updateLetterRects();
		};

		const handleScroll = () => {
			updateLetterRects();
		};

		window.addEventListener('mousemove', handleMouseMove, { passive: true });
		window.addEventListener('touchmove', handleTouchMove, { passive: true });
		window.addEventListener('resize', handleResize, { passive: true });
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('touchmove', handleTouchMove);
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('scroll', handleScroll);

			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [updateLetterSpacing, updateLetterRects]);

	return (
		<span ref={containerRef} className={className} style={style}>
			{wordElements.map((element) => {
				if (element.type === 'space') {
					return (
						<span
							key={element.key}
							className="inline-block"
							style={{ whiteSpace: 'pre' }}
						>
							{element.content}
						</span>
					);
				}

				return (
					<span key={element.key} className="inline-block whitespace-nowrap">
						{element.wordChars.map((char, charIndex) => {
							const currentIndex = element.wordStartIndex + charIndex;
							return (
								<span
									key={currentIndex}
									ref={(el) => {
										letterRefs.current[currentIndex] = el;
									}}
									className="inline-block transition-[letter-spacing] duration-100 ease-out"
									style={{ letterSpacing: `${minSpacing}em` }}
								>
									{char}
								</span>
							);
						})}
					</span>
				);
			})}
		</span>
	);
}
